import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useDocuments from 'hooks/document/useDocuments';
import DeleteIssuedDocumentModal from 'pages/private/owner/IssuedDocuments/components/DeleteIssuedDocumentModal';
import { CellProps } from 'react-table';
import { DocumentList } from 'types';
import { removeEmpty } from 'utils/functions';

const AthleteDocumentsTable = ({
  searchFilters,
  openSignModal,
  setDocumentId,
  permission = true,
  margin = 12,
  bgc = 'white',
}: {
  searchFilters: {
    search: string;
    type: string | null;
    isSigned: string | null;
  };
  openSignModal: () => void;
  setDocumentId: (documentId: string) => void;
  permission?: boolean;
  margin?: number;
  bgc?: 'white' | 'gray';
}): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const [ordering, setOrdering] = useState('status');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<DocumentList[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentList>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const orderingValue = useMemo(() => {
    if (ordering === 'status') {
      return 'signedDate';
    }
    return ordering;
  }, [ordering]);

  const strippedFilters = removeEmpty(searchFilters);
  if ('isSigned' in strippedFilters) {
    strippedFilters.isSigned = strippedFilters.isSigned === 'True';
  }
  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useDocuments({
      ...strippedFilters,
      ordering: ascending ? orderingValue : `-${orderingValue}`,
      isTemplate: false,
    });

  const handleDocumentClick = (document: DocumentList): void => {
    setDocumentId(document.documentId);
    openSignModal();
  };

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const handleDelete = (document: DocumentList): void => {
    setSelectedDocument(document);
    openDeleteModal();
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Status',
      accessor: 'status' as const,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Badge color={row.original.signedDate ? 'green' : 'red'} variant='filled' size='xs'>
          {row.original.signedDate ? 'Signed' : 'Not Signed'}
        </Badge>
      ),
      minWidth: '75px',
      maxWidth: '75px',
    },
    {
      Header: isDesktop ? 'Description' : 'Desc',
      accessor: 'description' as const,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Text
          weight='bold'
          underline
          sx={{
            cursor: 'pointer',
            width: 'fit-content',
          }}
          onClick={() => handleDocumentClick(row.original)}
        >
          {row.original.description}
        </Text>
      ),
    },
    {
      Header: 'Type',
      accessor: 'type' as const,
    },
    {
      Header: 'Date Signed',
      accessor: 'signedDate' as const,
      Cell: ({ value }: CellProps<DocumentList>) => (value ? dayjs(value).format(dateFormat) : '-'),
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<DocumentList>) => {
        const items =
          row.original.signedDate === null
            ? [
                {
                  title: 'View',
                  onClick: () => handleDocumentClick(row.original),
                },
                {
                  title: 'Delete',
                  onClick: () => handleDelete(row.original),
                  color: 'red',
                },
              ]
            : [
                {
                  title: 'View',
                  onClick: () => handleDocumentClick(row.original),
                },
              ];

        return (
          <Group position='right' px='xs'>
            <Menu position='bottom-end' items={items} disabled={!permission} />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['description', 'status', 'signedDate']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
        margin={margin}
        bgc={bgc}
      />
      {selectedDocument && (
        <DeleteIssuedDocumentModal
          document={selectedDocument}
          opened={isDeleteModalOpen}
          closeHandler={closeDeleteModal}
        />
      )}
    </>
  );
};

export default AthleteDocumentsTable;
